import React, { useEffect } from 'react';
import { Wrench, AlertTriangle, Clock, Users, Package, ListChecks, Plus, Trash2, CalendarClock, FileSignature } from 'lucide-react';
import ImageUpload from './ImageUpload';
import SignaturePad from './SignaturePad';

// Configuração de fotos por tipo (exceto a foto do horímetro da execução,
// que reaproveita o campo end_hour_meter_photo já existente no formulário).
export const MAINT_PHOTOS = {
  manutencao_corretiva: [
    { key: 'foto_problema_encontrado', label: 'Evidência: Problema Encontrado *' },
    { key: 'foto_diagnostico_1', label: 'Evidência: Diagnóstico (1 de 2) *' },
    { key: 'foto_diagnostico_2', label: 'Evidência: Diagnóstico (2 de 2) *' },
    { key: 'foto_pecas_novas', label: 'Evidência: Peças Novas Aplicadas *' },
    { key: 'foto_maquina_local', label: 'Máquina no Local de Atendimento (corpo inteiro) *' },
  ],
  manutencao_preventiva: [
    { key: 'foto_pecas_novas_antes', label: 'Peças/Filtros/Lubrificantes Novos (antes da aplicação) *' },
    { key: 'foto_pecas_usadas', label: 'Peças/Filtros/Lubrificantes Usados (após remoção) *' },
    { key: 'foto_maquina_finalizada', label: 'Máquina Finalizada (após a manutenção) *' },
  ],
};

export const MAINT_FIELDS = {
  manutencao_corretiva: ['problema_reclamado', 'problema_encontrado', 'diagnostico', 'causa_raiz', 'prioridade_manutencao'],
  manutencao_preventiva: ['recomendacao_futura'],
};

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa' },
  { value: 'media', label: 'Média' },
  { value: 'alta', label: 'Alta' },
  { value: 'critica', label: 'Crítica' },
];

// Verifica (client-side) se a O.S. de manutenção pode ser FECHADA.
export function maintReadyToClose(form, det) {
  if (!det) return true;
  const fieldsOk = (MAINT_FIELDS[det] || []).every((f) => form[f] && String(form[f]).trim());
  const mp = form.maintenance_photos || {};
  const photosOk = (MAINT_PHOTOS[det] || []).every((p) => mp[p.key]);
  const horimetroOk = !!(form.end_hour_meter && form.end_hour_meter_photo);
  const materialsOk = !(Array.isArray(form.materials) && form.materials.length) || !!form.materials_photo;
  // Fase 5 — assinatura combinada (desenho + nome do executor)
  const signOk = !!((form.assinatura_desenho_dataurl || form.assinatura_desenho_url) && form.assinatura_executor_nome && String(form.assinatura_executor_nome).trim());
  return fieldsOk && photosOk && horimetroOk && materialsOk && signOk;
}

const FIELD_LABELS = {
  problema_reclamado: 'Problema Reclamado',
  problema_encontrado: 'Problema Encontrado',
  diagnostico: 'Diagnóstico',
  causa_raiz: 'Causa Raiz do Problema',
  prioridade_manutencao: 'Prioridade da Manutenção',
  recomendacao_futura: 'Recomendação Futura',
};

// Lista legível de itens ainda pendentes para FECHAR a O.S. de manutenção.
export function maintMissingItems(form, det) {
  if (!det) return [];
  const missing = [];
  for (const f of MAINT_FIELDS[det] || []) {
    if (!(form[f] && String(form[f]).trim())) missing.push(FIELD_LABELS[f] || f);
  }
  const mp = form.maintenance_photos || {};
  for (const p of MAINT_PHOTOS[det] || []) {
    if (!mp[p.key]) missing.push(`Foto: ${p.label.replace(' *', '')}`);
  }
  if (!form.end_hour_meter) missing.push('Horímetro Final');
  if (!form.end_hour_meter_photo) missing.push('Foto: Horímetro Final');
  if (Array.isArray(form.materials) && form.materials.length && !form.materials_photo) {
    missing.push('Foto: Peças/Materiais aplicados');
  }
  // Fase 5 — assinatura combinada
  if (!(form.assinatura_desenho_dataurl || form.assinatura_desenho_url)) missing.push('Assinatura desenhada (executor)');
  if (!(form.assinatura_executor_nome && String(form.assinatura_executor_nome).trim())) missing.push('Nome do responsável pela execução');
  return missing;
}

const inputCls = 'w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors';

function dtLocal(v) {
  if (!v) return '';
  try { return v.slice(0, 16); } catch { return ''; }
}

export default function MaintenanceFields({ form, setForm, osDetalhado, canEditExecParada, colaboradores = [], almoxItens = [], fornecedores = [], checklistItems = [], locations = [], tiposServico = [], defaultExecutorName = '', requerSenha = true }) {
  // Inicializa o checklist a partir do template do tenant (apenas se ainda vazio)
  useEffect(() => {
    if (osDetalhado && (!form.post_checklist || form.post_checklist.length === 0) && checklistItems.length > 0) {
      setForm((f) => ({
        ...f,
        post_checklist: checklistItems.map((label) => ({ item: label, status: '', observacao: '' })),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osDetalhado, checklistItems]);

  if (!osDetalhado) return null;
  const isCorretiva = osDetalhado === 'manutencao_corretiva';

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setPhoto = (key, url) =>
    setForm((f) => ({ ...f, maintenance_photos: { ...(f.maintenance_photos || {}), [key]: url } }));

  return (
    <div className="space-y-4 p-4 rounded-xl border border-orange-500/30 bg-orange-500/5" data-testid="maintenance-fields">
      <div className="flex items-center gap-2">
        <Wrench size={18} className="text-orange-400" />
        <h3 className="text-base font-semibold text-white">
          Manutenção {isCorretiva ? 'Corretiva' : 'Preventiva'}
        </h3>
      </div>

      {/* Tipo de Execução */}
      <div>
        <label className="block text-sm text-gray-400 mb-2">Tipo de Execução</label>
        <select
          value={form.tipo_execucao || 'proprio'}
          onChange={(e) => set({ tipo_execucao: e.target.value })}
          disabled={!canEditExecParada}
          data-testid="input-tipo-execucao"
          className={`${inputCls} disabled:opacity-60`}
        >
          <option value="proprio">Próprio (equipe interna)</option>
          <option value="terceiro">Terceiro (prestador)</option>
        </select>
        {!canEditExecParada && (
          <p className="text-gray-500 text-xs mt-1">Editável apenas por Master/Gestor.</p>
        )}
      </div>

      {/* Fase 3 — Serviço de Terceiro (gera Contas a Pagar no fechamento) */}
      {form.tipo_execucao === 'terceiro' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5" data-testid="terceiro-section">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Valor do Serviço de Terceiro (R$)</label>
            <input type="number" step="0.01" min="0" value={form.valor_servico_terceiro ?? ''}
              onChange={(e) => set({ valor_servico_terceiro: e.target.value === '' ? '' : parseFloat(e.target.value) })}
              data-testid="input-valor-terceiro" className={inputCls} placeholder="0,00" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Prestador (Fornecedor)</label>
            <select value={form.prestador_id || ''} onChange={(e) => set({ prestador_id: e.target.value })}
              data-testid="input-prestador" className={inputCls}>
              <option value="">Selecione...</option>
              {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <p className="sm:col-span-2 text-xs text-blue-300">Ao fechar a O.S., este valor gera um compromisso em Contas a Pagar vinculado a esta O.S.</p>
        </div>
      )}

      {/* Campos textuais por tipo */}
      {isCorretiva ? (
        <>
          <Field label="Problema Reclamado *" testId="input-problema-reclamado"
            value={form.problema_reclamado} onChange={(v) => set({ problema_reclamado: v })} />
          <Field label="Problema Encontrado *" testId="input-problema-encontrado"
            value={form.problema_encontrado} onChange={(v) => set({ problema_encontrado: v })} />
          <Field label="Diagnóstico *" testId="input-diagnostico"
            value={form.diagnostico} onChange={(v) => set({ diagnostico: v })} />
          <Field label="Causa Raiz do Problema *" testId="input-causa-raiz"
            value={form.causa_raiz} onChange={(v) => set({ causa_raiz: v })} />
          <div>
            <label className="block text-sm text-gray-400 mb-2">Prioridade da Manutenção *</label>
            <select
              value={form.prioridade_manutencao || ''}
              onChange={(e) => set({ prioridade_manutencao: e.target.value })}
              data-testid="input-prioridade"
              className={inputCls}
            >
              <option value="">Selecione...</option>
              {PRIORIDADES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </>
      ) : (
        <Field label="Recomendação Futura *" testId="input-recomendacao-futura"
          value={form.recomendacao_futura} onChange={(v) => set({ recomendacao_futura: v })} />
      )}

      {/* Tempo de Parada da Máquina */}
      <div className="space-y-3 p-3 rounded-lg border border-gray-800 bg-[#0A0A0A]">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-blue-400" />
          <span className="text-sm font-medium text-white">Tempo de Parada da Máquina</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Início da Parada</label>
            <input
              type="datetime-local"
              value={dtLocal(form.data_hora_inicio_parada)}
              onChange={(e) => set({ data_hora_inicio_parada: e.target.value })}
              disabled={!canEditExecParada}
              data-testid="input-inicio-parada"
              className={`${inputCls} disabled:opacity-60`}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Fim da Parada</label>
            <input
              type="datetime-local"
              value={dtLocal(form.data_hora_fim_parada)}
              onChange={(e) => set({ data_hora_fim_parada: e.target.value })}
              disabled={!canEditExecParada}
              data-testid="input-fim-parada"
              className={`${inputCls} disabled:opacity-60`}
            />
          </div>
        </div>
        {form.duracao_parada_horas != null && form.duracao_parada_horas !== '' && (
          <p className="text-xs text-blue-300" data-testid="duracao-parada">
            Duração calculada: <strong>{form.duracao_parada_horas}h</strong>
          </p>
        )}
        {!canEditExecParada && (
          <p className="text-gray-500 text-xs">Início sugerido automaticamente. Edição manual: Master/Gestor.</p>
        )}
      </div>

      {/* Fase 2 — Mão de obra / Materiais / Checklist */}
      <LaborSection form={form} setForm={setForm} colaboradores={colaboradores} />
      <MaterialsSection form={form} setForm={setForm} almoxItens={almoxItens} />
      <CostSummary form={form} />
      <ChecklistSection form={form} setForm={setForm} />

      {/* Fase 4 — Diária do Colaborador */}
      <DiariaSection form={form} setForm={setForm} colaboradores={colaboradores} locations={locations} tiposServico={tiposServico} />

      {/* Fase 5 — Assinatura Combinada (obrigatória para fechar) */}
      <SignatureSection form={form} setForm={setForm} defaultExecutorName={defaultExecutorName} requerSenha={requerSenha} />

      {/* Fotos obrigatórias por tipo (a foto do horímetro da execução é a "Foto do Horímetro Final" abaixo) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <AlertTriangle size={14} className="text-orange-400" />
          Fotos obrigatórias ({isCorretiva ? '6' : '4'} no total — inclui a foto do horímetro)
        </div>
        {(MAINT_PHOTOS[osDetalhado] || []).map((p) => (
          <ImageUpload
            key={p.key}
            value={(form.maintenance_photos || {})[p.key]}
            onChange={(url) => setPhoto(p.key, url)}
            label={p.label}
            folder={`manutencao/${osDetalhado}`}
          />
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-2">{label}</label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        data-testid={testId}
        className={`${inputCls} resize-none`}
      />
    </div>
  );
}

const smallInput = 'w-full px-2 py-1.5 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm focus:border-yellow-500 focus:outline-none';

// ---------- Mão de obra (apontamento de horas) ----------
function calcHoras(ini, fim) {
  const toMin = (s) => {
    if (!s || !s.includes(':')) return null;
    const [h, m] = s.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };
  const a = toMin(ini), b = toMin(fim);
  if (a == null || b == null) return 0;
  let d = b - a;
  if (d < 0) d += 24 * 60;
  return Math.round((d / 60) * 100) / 100;
}

function LaborSection({ form, setForm, colaboradores }) {
  const rows = form.labor_entries || [];
  const update = (rows2) => setForm((f) => ({ ...f, labor_entries: rows2 }));
  const addRow = () => update([...rows, { colaborador_id: '', data: form.date || '', hora_inicio: '', hora_fim: '', total_horas: 0, observacoes: '' }]);
  const setRow = (i, patch) => update(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => update(rows.filter((_, idx) => idx !== i));
  const totalGeral = rows.reduce((s, r) => s + calcHoras(r.hora_inicio, r.hora_fim), 0);

  return (
    <div className="space-y-2 p-3 rounded-lg border border-gray-800 bg-[#0A0A0A]" data-testid="labor-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Users size={16} className="text-purple-400" /><span className="text-sm font-medium text-white">Mão de Obra (apontamento de horas)</span></div>
        <span className="text-xs text-purple-300">Total: <strong>{Math.round(totalGeral * 100) / 100}h</strong></span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`labor-row-${i}`}>
          <select value={r.colaborador_id || ''} onChange={(e) => setRow(i, { colaborador_id: e.target.value })} className={`${smallInput} col-span-3`} data-testid={`labor-colaborador-${i}`}>
            <option value="">Colaborador...</option>
            {colaboradores.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <input type="date" value={r.data || ''} onChange={(e) => setRow(i, { data: e.target.value })} className={`${smallInput} col-span-2`} />
          <input type="time" value={r.hora_inicio || ''} onChange={(e) => setRow(i, { hora_inicio: e.target.value })} className={`${smallInput} col-span-2`} data-testid={`labor-inicio-${i}`} />
          <input type="time" value={r.hora_fim || ''} onChange={(e) => setRow(i, { hora_fim: e.target.value })} className={`${smallInput} col-span-2`} data-testid={`labor-fim-${i}`} />
          <span className="col-span-2 text-xs text-center text-gray-300" data-testid={`labor-total-${i}`}>{calcHoras(r.hora_inicio, r.hora_fim)}h</span>
          <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-red-400 hover:text-red-300 flex justify-center" data-testid={`labor-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={addRow} className="flex items-center gap-1 text-xs text-purple-300 hover:text-purple-200" data-testid="labor-add-row"><Plus size={14} /> Adicionar apontamento</button>
    </div>
  );
}

// ---------- Materiais aplicados ----------
export function MaterialsSection({ form, setForm, almoxItens, showPhoto = true }) {
  const rows = form.materials || [];
  const update = (rows2) => setForm((f) => ({ ...f, materials: rows2 }));
  const addRow = () => update([...rows, { origem: 'almoxarifado', item_id: '', item_codigo: '', descricao: '', quantidade: 1, unidade: '', valor_unitario: '' }]);
  const setRow = (i, patch) => update(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => update(rows.filter((_, idx) => idx !== i));

  const onSelectItem = (i, itemId) => {
    const it = almoxItens.find((x) => x.id === itemId);
    setRow(i, { item_id: itemId, item_codigo: it?.codigo || '', descricao: it?.nome || '', unidade: it?.unidade_medida || '' });
  };

  return (
    <div className="space-y-2 p-3 rounded-lg border border-gray-800 bg-[#0A0A0A]" data-testid="materials-section">
      <div className="flex items-center gap-2"><Package size={16} className="text-amber-400" /><span className="text-sm font-medium text-white">Peças e Materiais Aplicados</span></div>
      {rows.map((r, i) => (
        <div key={i} className="space-y-2 p-2 rounded border border-gray-800" data-testid={`material-row-${i}`}>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs text-gray-300"><input type="radio" checked={r.origem === 'almoxarifado'} onChange={() => setRow(i, { origem: 'almoxarifado', descricao: '', item_id: '' })} data-testid={`material-origem-almox-${i}`} /> Almoxarifado</label>
            <label className="flex items-center gap-1 text-xs text-gray-300"><input type="radio" checked={r.origem === 'avulsa'} onChange={() => setRow(i, { origem: 'avulsa', item_id: '', item_codigo: '' })} data-testid={`material-origem-avulsa-${i}`} /> Avulsa</label>
            <button type="button" onClick={() => removeRow(i)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`material-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-12 gap-2">
            {r.origem === 'almoxarifado' ? (
              <select value={r.item_id || ''} onChange={(e) => onSelectItem(i, e.target.value)} className={`${smallInput} col-span-7`} data-testid={`material-item-${i}`}>
                <option value="">Selecione o item...</option>
                {almoxItens.map((it) => <option key={it.id} value={it.id}>{it.codigo ? `${it.codigo} — ` : ''}{it.nome} (saldo {it.saldo_atual})</option>)}
              </select>
            ) : (
              <input type="text" value={r.descricao || ''} onChange={(e) => setRow(i, { descricao: e.target.value })} placeholder="Descrição do material" className={`${smallInput} col-span-7`} data-testid={`material-descricao-${i}`} />
            )}
            <input type="number" step="0.01" min="0" value={r.quantidade ?? ''} onChange={(e) => setRow(i, { quantidade: e.target.value === '' ? '' : parseFloat(e.target.value) })} placeholder="Qtd" className={`${smallInput} col-span-2`} data-testid={`material-qtd-${i}`} />
            {r.origem === 'avulsa' ? (
              <input type="number" step="0.01" min="0" value={r.valor_unitario ?? ''} onChange={(e) => setRow(i, { valor_unitario: e.target.value === '' ? '' : parseFloat(e.target.value) })} placeholder="Vlr unit." className={`${smallInput} col-span-3`} data-testid={`material-valor-${i}`} />
            ) : (
              <span className="col-span-3 text-xs text-gray-500 self-center">Baixa no estoque ao fechar</span>
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={addRow} className="flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200" data-testid="material-add-row"><Plus size={14} /> Adicionar material</button>
      {showPhoto && rows.length > 0 && (
        <div className="pt-1">
          <ImageUpload
            value={form.materials_photo}
            onChange={(url) => setForm((f) => ({ ...f, materials_photo: url }))}
            label="Foto Evidência: Peças/Materiais (obrigatória) *"
            folder="manutencao/materiais"
          />
        </div>
      )}
    </div>
  );
}

// ---------- Resumo de custo (preview) ----------
function CostSummary({ form }) {
  const avulsos = (form.materials || [])
    .filter((m) => m.origem === 'avulsa')
    .reduce((s, m) => s + (parseFloat(m.quantidade) || 0) * (parseFloat(m.valor_unitario) || 0), 0);
  const terceiro = form.tipo_execucao === 'terceiro' ? (parseFloat(form.valor_servico_terceiro) || 0) : 0;
  const total = form.custo_total_manutencao != null && form.integracao_financeira_aplicada
    ? form.custo_total_manutencao
    : avulsos + terceiro;
  const fmt = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return (
    <div className="p-3 rounded-lg border border-gray-800 bg-[#0A0A0A] text-sm" data-testid="cost-summary">
      <div className="flex items-center justify-between text-gray-400"><span>Materiais avulsos</span><span>{fmt(avulsos)}</span></div>
      {terceiro > 0 && <div className="flex items-center justify-between text-gray-400"><span>Mão de obra (terceiro)</span><span>{fmt(terceiro)}</span></div>}
      <div className="flex items-center justify-between text-white font-semibold mt-1 pt-1 border-t border-gray-800">
        <span>Custo Total (estimado)</span><span data-testid="cost-total">{fmt(total)}</span>
      </div>
      <p className="text-[11px] text-gray-600 mt-1">Materiais do almoxarifado entram pelo custo de estoque ao fechar. Valor definitivo é calculado no fechamento.</p>
    </div>
  );
}

// ---------- Checklist pós-manutenção ----------
function ChecklistSection({ form, setForm }) {
  const rows = form.post_checklist || [];
  const setRow = (i, patch) => setForm((f) => ({ ...f, post_checklist: (f.post_checklist || []).map((r, idx) => idx === i ? { ...r, ...patch } : r) }));
  if (rows.length === 0) return null;
  return (
    <div className="space-y-2 p-3 rounded-lg border border-gray-800 bg-[#0A0A0A]" data-testid="checklist-section">
      <div className="flex items-center gap-2"><ListChecks size={16} className="text-green-400" /><span className="text-sm font-medium text-white">Checklist Pós-Manutenção</span></div>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`checklist-row-${i}`}>
          <span className="flex-1 text-xs text-gray-300">{r.item}</span>
          <button type="button" onClick={() => setRow(i, { status: 'ok' })} className={`px-2 py-1 rounded text-xs ${r.status === 'ok' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400'}`} data-testid={`checklist-ok-${i}`}>OK</button>
          <button type="button" onClick={() => setRow(i, { status: 'nao_ok' })} className={`px-2 py-1 rounded text-xs ${r.status === 'nao_ok' ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400'}`} data-testid={`checklist-nok-${i}`}>Não OK</button>
          <input type="text" value={r.observacao || ''} onChange={(e) => setRow(i, { observacao: e.target.value })} placeholder="Obs." className={`${smallInput} w-32`} data-testid={`checklist-obs-${i}`} />
        </div>
      ))}
    </div>
  );
}


// ---------- Diária do Colaborador (Fase 4) ----------
function calcHorasDiaria(d) {
  // Diferença em horas entre (data_inicio + hora_inicio) e (data_fim + hora_fim)
  try {
    const di = new Date(`${d.data_inicio_servico}T${d.hora_inicio_servico || '00:00'}`);
    const df = new Date(`${d.data_fim_servico}T${d.hora_fim_servico || '00:00'}`);
    if (isNaN(di) || isNaN(df)) return 0;
    const diff = (df - di) / 3600000;
    return diff > 0 ? Math.round(diff * 100) / 100 : 0;
  } catch {
    return 0;
  }
}

const STATUS_DIARIA_LABEL = {
  rascunho: { label: 'Rascunho', cls: 'bg-gray-700 text-gray-200' },
  aguardando_aprovacao: { label: 'Aguardando Aprovação', cls: 'bg-yellow-600 text-white' },
  aprovada: { label: 'Aprovada', cls: 'bg-green-600 text-white' },
  rejeitada: { label: 'Rejeitada', cls: 'bg-red-600 text-white' },
};

function DiariaSection({ form, setForm, colaboradores, locations, tiposServico }) {
  const gerar = !!form.gerar_diaria;
  const d = form.diaria || {};
  const status = d.status || 'rascunho';
  const aprovada = status === 'aprovada';
  const horas = calcHorasDiaria(d);

  const setDiaria = (patch) => setForm((f) => ({ ...f, diaria: { ...(f.diaria || {}), ...patch } }));
  const toggleGerar = (checked) => {
    setForm((f) => ({
      ...f,
      gerar_diaria: checked,
      diaria: checked ? { ...(f.diaria || {}) } : (f.diaria || null),
    }));
  };

  const stCfg = STATUS_DIARIA_LABEL[status] || STATUS_DIARIA_LABEL.rascunho;

  return (
    <div className="space-y-3 p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5" data-testid="diaria-section">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={gerar}
          disabled={aprovada}
          onChange={(e) => toggleGerar(e.target.checked)}
          data-testid="diaria-gerar-checkbox"
          className="w-4 h-4 accent-emerald-500"
        />
        <CalendarClock size={16} className="text-emerald-400" />
        <span className="text-sm font-medium text-white">Gerar Diária para Colaborador</span>
        {gerar && (
          <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${stCfg.cls}`} data-testid="diaria-status-badge">
            {stCfg.label}
          </span>
        )}
      </label>

      {gerar && (
        <div className="space-y-3" data-testid="diaria-fields">
          <p className="text-[11px] text-emerald-300/80">
            Ao fechar a O.S., a diária é enviada para <strong>aprovação</strong>. O valor é calculado por Master/Gestor
            usando o <em>valor/hora de serviço</em> cadastrado no colaborador.
          </p>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Colaborador *</label>
            <select
              value={d.colaborador_id || ''}
              onChange={(e) => setDiaria({ colaborador_id: e.target.value })}
              disabled={aprovada}
              data-testid="diaria-colaborador"
              className={`${inputCls} disabled:opacity-60`}
            >
              <option value="">Selecione...</option>
              {colaboradores.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Data Início *</label>
              <input type="date" value={d.data_inicio_servico || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ data_inicio_servico: e.target.value })}
                data-testid="diaria-data-inicio" className={`${inputCls} disabled:opacity-60`} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Hora Início *</label>
              <input type="time" value={d.hora_inicio_servico || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ hora_inicio_servico: e.target.value })}
                data-testid="diaria-hora-inicio" className={`${inputCls} disabled:opacity-60`} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Data Fim *</label>
              <input type="date" value={d.data_fim_servico || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ data_fim_servico: e.target.value })}
                data-testid="diaria-data-fim" className={`${inputCls} disabled:opacity-60`} />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Hora Fim *</label>
              <input type="time" value={d.hora_fim_servico || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ hora_fim_servico: e.target.value })}
                data-testid="diaria-hora-fim" className={`${inputCls} disabled:opacity-60`} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Local de Execução</label>
              <select value={d.local_execucao_id || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ local_execucao_id: e.target.value })}
                data-testid="diaria-local" className={`${inputCls} disabled:opacity-60`}>
                <option value="">Selecione...</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name || l.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Tipo de Serviço</label>
              <select value={d.tipo_servico || ''} disabled={aprovada}
                onChange={(e) => setDiaria({ tipo_servico: e.target.value })}
                data-testid="diaria-tipo-servico" className={`${inputCls} disabled:opacity-60`}>
                <option value="">Selecione...</option>
                {tiposServico.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Observações</label>
            <textarea value={d.observacoes || ''} disabled={aprovada} rows={2}
              onChange={(e) => setDiaria({ observacoes: e.target.value })}
              data-testid="diaria-observacoes" className={`${inputCls} resize-none disabled:opacity-60`} />
          </div>

          <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-800">
            <span className="text-gray-400">Horas de serviço (calculadas)</span>
            <span className="text-emerald-300 font-semibold" data-testid="diaria-horas-calc">{horas}h</span>
          </div>

          {aprovada && (
            <div className="text-xs text-emerald-300" data-testid="diaria-aprovada-info">
              Aprovada por {d.aprovada_por || '—'} • Valor: {(d.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
          )}
          {status === 'rejeitada' && d.motivo_rejeicao && (
            <div className="text-xs text-red-300" data-testid="diaria-rejeitada-info">
              Rejeitada: {d.motivo_rejeicao}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------- Assinatura Combinada de Fechamento (Fase 5) ----------
function SignatureSection({ form, setForm, defaultExecutorName, requerSenha = true }) {
  // Pré-preenche o nome do executor com o operador de acompanhamento (editável, sem travas)
  useEffect(() => {
    if (defaultExecutorName && !form.assinatura_executor_nome) {
      setForm((f) => ({ ...f, assinatura_executor_nome: defaultExecutorName }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultExecutorName]);

  // Regra vem do BACKEND (requer_senha_fechamento): O.S. reaberta exige NOVA senha,
  // mesmo tendo assinatura anterior aprovada (preservada no histórico).
  const jaAssinada = !!(form.assinatura_fechamento && form.assinatura_fechamento.aprovado_em) && !requerSenha;
  const foiReaberta = !!(form.assinatura_fechamento && form.assinatura_fechamento.aprovado_em) && requerSenha;

  return (
    <div className="space-y-3 p-4 rounded-xl border border-violet-500/30 bg-violet-500/5" data-testid="signature-section">
      <div className="flex items-center gap-2">
        <FileSignature size={18} className="text-violet-400" />
        <h3 className="text-base font-semibold text-white">Assinatura de Fechamento</h3>
        <span className="text-xs text-violet-300/80">(obrigatória para fechar a O.S.)</span>
      </div>

      {jaAssinada ? (
        <div className="text-sm text-violet-200 space-y-1" data-testid="signature-done">
          <p>✅ O.S. já assinada e aprovada.</p>
          <p className="text-xs text-gray-400">
            Executor: <strong>{form.assinatura_fechamento.executor_nome || '—'}</strong> •
            Aprovado por: <strong>{form.assinatura_fechamento.aprovador_nome || '—'}</strong>
          </p>
          {form.assinatura_fechamento.assinatura_desenho_url && (
            <img src={form.assinatura_fechamento.assinatura_desenho_url} alt="Assinatura" className="h-16 bg-white rounded p-1 mt-1" />
          )}
        </div>
      ) : (
        <>
          {foiReaberta && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2" data-testid="signature-reopened-note">
              O.S. reaberta — nova assinatura e confirmação por senha são obrigatórias para fechar novamente.
              A assinatura anterior permanece registrada no histórico de auditoria.
            </div>
          )}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Responsável pela Execução / Acompanhamento *</label>
            <input
              type="text"
              value={form.assinatura_executor_nome || ''}
              onChange={(e) => setForm((f) => ({ ...f, assinatura_executor_nome: e.target.value }))}
              data-testid="signature-executor-name"
              placeholder="Nome de quem executou/acompanhou o serviço"
              className={inputCls}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Assinatura Digital (desenho) *</label>
            <SignaturePad
              value={form.assinatura_desenho_dataurl || form.assinatura_desenho_url}
              onChange={(dataUrl) => setForm((f) => ({ ...f, assinatura_desenho_dataurl: dataUrl }))}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Confirmação por Senha (usuário logado) *</label>
            <input
              type="password"
              value={form.confirmacao_senha || ''}
              onChange={(e) => setForm((f) => ({ ...f, confirmacao_senha: e.target.value }))}
              data-testid="signature-password"
              placeholder="Sua senha de acesso ao sistema"
              autoComplete="off"
              className={inputCls}
            />
            <p className="text-xs text-gray-500 mt-1">
              A senha confirma a aprovação/recebimento do serviço e vincula o fechamento ao seu usuário.
              É exigida somente no momento de fechar a O.S.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
